/* eslint-disable max-lines */

import React from 'react';
import { useSelector, connect } from 'react-redux';
import { Dispatch } from 'redux';
import { getPrimaryPrice, getProductImages, getDisplayedName, isWebSellable } from 'features/product-info';
import { NavigationMappedState, PdpNavigationProps, Section } from 'features/page-navigation/types';
import { PdpAddToBasket } from 'features/product-info/components/pdp-add-to-basket';
import { getIsGoodSupplyDataLoaded, isWebAvailable as getIsWebAvailable } from 'features/goods-supply';
import { Breakpoint, ConnectedProps } from 'types/utils';
import { getReviewsRatingTotal } from 'features/reviews/selectors';
import { RootReducer } from 'store/root-reducer';
import { cx } from 'linaria';
import { getArticle } from 'common/selectors';
import { addMediaQueryHandlers } from 'utils/add-media-query-handlers';
import { useOptimizedImages } from 'common/hooks/use-optimized-images';
import { Styles } from './styles';
import { GA_STICKY_PANEL_BUTTON_CLICK } from '../ducks/page-navigation';
import { getSectionsMetaCollection } from '../ducks/selectors';
import { getCartItem, getMeasurementSettings } from '../../basket';
import { StickyPanelConnected } from './sticky-panel-with-navigation';
import { AvailabilityState, ProductBlocks, StickyPanel } from './sticky-panel';
import { MQ } from './sticky-panel/media-queries';
import { ContentBlocks } from './content-blocks';
import { sortSectionMeta } from './utils';

export const Navigation: React.FC<
  PdpNavigationProps & ConnectedProps<NavigationMappedState, typeof mapDispatchToProps>
> = ({
  id,
  className,
  quantity,
  gaStickyPanelButtonClick,
  measurementSettings,
  isAvailableForPurchase,
  isSupplyDataLoaded,
}) => {
  const pdpSectionMeta = useSelector(getSectionsMetaCollection);
  const reviewsCount = useSelector(getReviewsRatingTotal)?.count;
  const { price, currency } = useSelector(getPrimaryPrice);
  const href = '#pdp-showcase';
  const images = useSelector(getProductImages);
  const productDescription = useSelector(getDisplayedName) || '';
  const availabilityState: AvailabilityState = isAvailableForPurchase
    ? {
        availability: 'available',

        price: price!,
      }
    : { availability: 'out-of-stock' };

  const imgSrc = images?.[0].url;
  const imgSizes = {
    thumb: {
      w: '80',
      h: '80',
    },
  };

  const productImage = useOptimizedImages(imgSrc, imgSizes);

  const [breakpoint, setBreakpoint] = React.useState<Breakpoint | null>(null);
  React.useEffect(
    () =>
      addMediaQueryHandlers(
        [MQ.forMobile, MQ.forTablet, MQ.forMedium, MQ.forLarge],
        ['mobile', 'tablet', 'medium', 'large'],
        null,
        setBreakpoint,
      ),
    [],
  );
  const sections = React.useMemo(() => {
    let result: Array<{
      id: string;
      title: string;
      badgeNumber?: number;
    }> = sortSectionMeta(pdpSectionMeta).flatMap((item) => {
      return item.navigationId
        ? {
            id: item.navigationId,
            title: item.title,
          }
        : [];
    });

    // change section order when changing quantity
    const complementsIndex = result.findIndex((item) => item.id === 'complements');
    if (quantity > 0 && complementsIndex !== -1) {
      const complementsSection = result[complementsIndex];
      for (let i = complementsIndex; i >= 1; i--) {
        result[i] = result[i - 1];
      }
      result[0] = complementsSection;
    }

    if (reviewsCount) {
      let reviewsSectionIdx = result.findIndex((item) => item.id === 'reviews');
      if (reviewsSectionIdx !== -1) {
        result[reviewsSectionIdx] = { ...result[reviewsSectionIdx], badgeNumber: reviewsCount };
      }
    }

    const notSellableProductNavigationMap = new Map([
      ['substitutes', 0],
      ['collectionProducts', 1],
      ['complements', 2],
      ['description', 3],
      ['characteristics', 4],
      ['reviews', 5],
    ]);

    // rearrange navigation if product is not sellable
    if (isSupplyDataLoaded && !isAvailableForPurchase) {
      let rearrangedResult = new Array(6).fill(undefined);
      for (const link of result) {
        const index = notSellableProductNavigationMap.get(link?.id);
        if (typeof index !== 'undefined') {
          rearrangedResult[index] = link;
        }
      }

      result = rearrangedResult.filter((i) => !!i);
    }

    return result;
  }, [pdpSectionMeta, quantity, reviewsCount, isSupplyDataLoaded, isAvailableForPurchase]);

  const hrefSections = React.useMemo(
    () =>
      sections.map((item) => {
        return {
          href: `#${item.id}`,
          title: item.title,
          badgeNumber: item.badgeNumber,
        };
      }),
    [sections],
  );

  return (
    <>
      {sections.length > 0 && breakpoint == null && (
        <StickyPanel
          data-qa="navigation-sticky"
          breakpoint="large"
          display="only-navigation"
          sections={hrefSections}
          activeSectionHref={hrefSections[0].href}
          productImageUrl={productImage?.thumb.x || ''}
          productDescription={productDescription}
          availabilityState={availabilityState}
          onSectionActivated={gaStickyPanelButtonClick}
        />
      )}
      {sections.length > 0 && breakpoint && (
        <StickyPanelConnected
          data-qa="navigation_mf-pdp"
          breakpoint={breakpoint}
          id={id}
          className={cx(Styles.stickyPanelWithNavigation, className)}
          sections={sections}
          productImageUrl={productImage?.thumb.x || ''}
          productDescription={productDescription}
          slots={{
            image: (
              <ContentBlocks.LinkWrapper data-qa="navigation-product-image_mf-pdp" href={href}>
                <ProductBlocks.Image
                  description={productDescription}
                  onClick={() => gaStickyPanelButtonClick({ title: 'Товар', href })}
                  url={productImage?.thumb.x || ''}
                />
              </ContentBlocks.LinkWrapper>
            ),
            price: availabilityState.availability === 'available' &&
              typeof availabilityState.price === 'number' && (
                <ContentBlocks.LinkWrapper data-qa="navigation-product-price_mf-pdp" href={href}>
                  <ContentBlocks.PriceView
                    onClick={() => gaStickyPanelButtonClick({ title: 'Цена', href })}
                    price={availabilityState.price}
                    currencySlug={currency || ''}
                  />
                </ContentBlocks.LinkWrapper>
              ),
            description: (
              <ContentBlocks.LinkWrapper data-qa="navigation-product-description_mf-pdp" href={href}>
                <ProductBlocks.Description description={productDescription} />
              </ContentBlocks.LinkWrapper>
            ),
            basketItem: measurementSettings && (
              <PdpAddToBasket
                className={Styles.addToBasket}
                classnameCounter={Styles.counter}
                data-qa="navigation-basket-item_mf-pdp"
                isSwapPrice={false}
                size="m"
                uom=""
              />
            ),
          }}
          availabilityState={availabilityState}
          onSectionActivated={gaStickyPanelButtonClick}
        />
      )}
    </>
  );
};
const mapStoreToProps = (store: RootReducer): NavigationMappedState => {
  const productId = getArticle(store);
  const quantity = getCartItem(productId)(store)?.quantity || 0;
  const measurementSettings = getMeasurementSettings(store);
  const isWebAvailable = getIsWebAvailable(store);
  const isSellable = isWebSellable(store);
  const isSupplyDataLoaded = getIsGoodSupplyDataLoaded(store);

  const isAvailableForPurchase = isWebAvailable && isSellable;

  return {
    quantity,
    measurementSettings,
    isAvailableForPurchase,
    isSupplyDataLoaded,
  };
};
const mapDispatchToProps = (dispatch: Dispatch) => ({
  gaStickyPanelButtonClick: ({ title }: Section) => dispatch(GA_STICKY_PANEL_BUTTON_CLICK({ title })),
});
export const PdpNavigation = connect(mapStoreToProps, mapDispatchToProps)(Navigation);
